import { enhancedTmsApiService } from '#api/enhancements/enhancedTmsApiService'
import type { EnhancedProductGroupId } from '#api/enhancements/enhancedTmsApiTypes'
import {
  addCompatibilityEntries,
  checkGroupCompatibility,
  clearCompatibilityRequests,
  type LocalConfigurationId,
} from '#app/store/slices/configurationSlice'
import { toastError } from '#features/ErrorHandling/toastError'
import type { AppListenerApi, AppMiddleware } from '../configurationRequestMiddleware'

async function updateCompatibility(configurationId: LocalConfigurationId, groupId: EnhancedProductGroupId, listenerApi: AppListenerApi) {
  // pulling the original state should give an indicator if the request has to be called
  // if the requested product group is not in it (or the compat object doesn't exist), then it's time to recall
  const state = listenerApi.getOriginalState()
  const configuration = state.truckConfigurations.configurations[configurationId]
  if (!configuration?.uuid) {
    return
  }
  const hasBeenRequested = configuration.compatibility?.requestedProductGroups.includes(groupId) ?? false
  const queryVersion = configuration.version
  const matchesVersion = configuration.compatibility?.version === queryVersion
  // if it has been requested can be determined if the version of the compat object matches
  if (hasBeenRequested && matchesVersion) {
    return
  }

  try {
    await listenerApi
      .dispatch(
        enhancedTmsApiService.endpoints.putApiConfigurationByUuidChangeGroupOfInterest.initiate({
          uuid: configuration.uuid,
          groupForCompatibilityCheck: groupId,
        }),
      )
      .unwrap()

    const mostRecentVersion = listenerApi.getState().truckConfigurations.configurations[configurationId]?.version
    const hasVersionChanged = mostRecentVersion !== queryVersion
    if (hasVersionChanged) {
      console.warn(`Configuration version changed from ${queryVersion} to ${mostRecentVersion} while checking compatibility for group ${groupId}.`)
      listenerApi.dispatch(clearCompatibilityRequests({ id: configuration.id, version: queryVersion, groupId }))
      return
    }

    listenerApi.dispatch(
      addCompatibilityEntries({
        id: configuration.id,
        version: configuration.version,
        checkedProductGroup: groupId,
      }),
    )
  } catch (e) {
    console.error('Configuration compatibility check failed', e)
    toastError(e, { customMessage: 'Configuration compatibility check failed' })
    listenerApi.dispatch(clearCompatibilityRequests({ id: configuration.id, version: queryVersion, groupId }))
  }
}

export const configurationCompatibilityListener = (middleware: AppMiddleware) => {
  middleware.startListening({
    matcher: checkGroupCompatibility.match,
    effect: async (action, listenerApi) => {
      if (!checkGroupCompatibility.match(action) || !action.payload.id) {
        return
      }

      void updateCompatibility(action.payload.id, action.payload.groupId, listenerApi)
    },
  })
}
